import React, { useState, useEffect } from 'react';
import AttitudeIndicator from './AttitudeIndicator';
import { Box, Typography, Tooltip, useTheme } from '@mui/material';

const HUD_SIZE = 120;

const InfoRow = ({ label, value, unit }) => (
    <Box
        sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            gap: 1,
            lineHeight: 1.2,
        }}
    >
        <Typography
            component="span"
            sx={{
                fontSize: 9,
                fontWeight: 600,
                letterSpacing: 0.5,
                color: 'rgba(255,255,255,0.65)',
                textTransform: 'uppercase',
            }}
        >
            {label}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.25 }}>
            <Typography
                component="span"
                sx={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: '#fff',
                    fontVariantNumeric: 'tabular-nums',
                }}
            >
                {value}
            </Typography>
            {unit && (
                <Typography
                    component="span"
                    sx={{ fontSize: 9, color: 'rgba(255,255,255,0.6)' }}
                >
                    {unit}
                </Typography>
            )}
        </Box>
    </Box>
);

// Vertical RSSI indicator: small column with label / bar / percent.
// percent: 0..100 or null (unknown). Stretches vertically to fill its flex parent.
const RssiBar = ({ label, percent, tooltip }) => {
    const known = typeof percent === 'number' && !Number.isNaN(percent);
    const pct = known ? Math.max(0, Math.min(100, percent)) : 0;
    let color = 'rgba(110,110,110,0.7)';
    if (known) {
        if (pct >= 60) color = 'rgba(46, 160, 67, 0.9)';
        else if (pct >= 30) color = 'rgba(210, 153, 34, 0.9)';
        else color = 'rgba(220, 53, 69, 0.95)';
    }    const cell = (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 0.75,
                py: 0.5,
                width: 32,
            }}
        >
            <Box
                sx={{
                    flex: 1,
                    width: 6,
                    bgcolor: 'rgba(255,255,255,0.12)',
                    borderRadius: 0.5,
                    position: 'relative',
                    overflow: 'hidden',
                    mb: 0.375,
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        bottom: 0,
                        height: `${pct}%`,
                        bgcolor: color,
                        transition: 'height 0.2s ease',
                    }}
                />
            </Box>
            <Typography
                sx={{
                    fontSize: 9,
                    fontWeight: 700,
                    letterSpacing: 0.5,
                    color: 'rgba(255,255,255,0.7)',
                    lineHeight: 1,
                }}
            >
                {label}
            </Typography>
        </Box>
    );
    return tooltip ? <Tooltip title={tooltip} arrow placement="top">{cell}</Tooltip> : cell;
};

const StatusChip = ({ label, state, tooltip }) => {
    // state: 'ok' | 'warn' | 'bad' | 'unknown'
    const palette = {
        ok:      { bg: 'rgba(46, 160, 67, 0.85)',  fg: '#fff' },
        warn:    { bg: 'rgba(210, 153, 34, 0.9)',  fg: '#000' },
        bad:     { bg: 'rgba(220, 53, 69, 0.95)',  fg: '#fff' },
        unknown: { bg: 'rgba(110, 110, 110, 0.8)', fg: '#eee' },
    }[state] || { bg: 'rgba(110, 110, 110, 0.8)', fg: '#eee' };
    const chip = (
        <Box
            sx={{
                px: 0.75,
                py: 0.125,
                borderRadius: 0.75,
                bgcolor: palette.bg,
                color: palette.fg,
                fontSize: 9,
                fontWeight: 700,
                letterSpacing: 0.5,
                textTransform: 'uppercase',
                lineHeight: 1.4,
                whiteSpace: 'nowrap',
            }}
        >
            {label}
        </Box>
    );
    return tooltip ? <Tooltip title={tooltip} arrow placement="top">{chip}</Tooltip> : chip;
};

const Hud = ({ mavTimestamp, selectedMavId }) => {
    const [heading, setHeading] = useState(0);
    const [altitude, setAltitude] = useState(0);
    const [relativeAltitude, setRelativeAltitude] = useState(0);
    const [groundspeed, setGroundspeed] = useState(0);
    const [airspeed, setAirspeed] = useState(0);
    const [climb, setClimb] = useState(0);
    const [throttle, setThrottle] = useState(0);
    const [batteryVoltage, setBatteryVoltage] = useState(null);
    const [batteryRemaining, setBatteryRemaining] = useState(null);
    const [satellites, setSatellites] = useState(null);
    const [armed, setArmed] = useState(false);
    const [ekfStatus, setEkfStatus] = useState({ state: 'unknown', tip: 'EKF data unavailable' });
    const [vibeStatus, setVibeStatus] = useState({ state: 'unknown', tip: 'Vibration data unavailable' });
    const [rcRssi, setRcRssi] = useState(null);       // 0..100 or null
    const [tlmRssi, setTlmRssi] = useState(null);     // 0..100 or null (local)
    const [tlmRemRssi, setTlmRemRssi] = useState(null); // 0..100 or null (remote)
    const theme = useTheme();

    useEffect(() => {
        const vehicle = window.mav?.[selectedMavId];
        if (!vehicle) return;
        if (vehicle.VFR_HUD !== undefined) {
            setHeading(vehicle.VFR_HUD.heading);
            setAltitude(vehicle.VFR_HUD.alt);
            setGroundspeed(vehicle.VFR_HUD.groundspeed);
            setAirspeed(vehicle.VFR_HUD.airspeed);
            setClimb(vehicle.VFR_HUD.climb);
            setThrottle(vehicle.VFR_HUD.throttle);
        }
        if (vehicle.SYS_STATUS !== undefined) {
            setBatteryVoltage(vehicle.SYS_STATUS.voltage_battery / 1000);
            setBatteryRemaining(vehicle.SYS_STATUS.battery_remaining);
        }
        if (vehicle.GPS_RAW_INT !== undefined) {
            setSatellites(vehicle.GPS_RAW_INT.satellites_visible);
        }
        if (vehicle.GLOBAL_POSITION_INT !== undefined) {
            setRelativeAltitude(vehicle.GLOBAL_POSITION_INT.relative_alt / 1000);
        }
        if (vehicle.HEARTBEAT !== undefined) {
            const isArmed = (vehicle.HEARTBEAT.base_mode & 0x80) !== 0;
            setArmed(isArmed);
        }
        if (vehicle.EKF_STATUS_REPORT !== undefined) {
            const r = vehicle.EKF_STATUS_REPORT;
            const variances = [
                r.velocity_variance,
                r.pos_horiz_variance,
                r.pos_vert_variance,
                r.compass_variance,
            ].filter((v) => typeof v === 'number');
            const maxV = variances.length ? Math.max(...variances) : 0;
            const flags = r.flags || 0;
            const uninitialized = (flags & 0x200) !== 0; // EKF_UNINITIALIZED
            let state = 'ok';
            let tip = `EKF variance ${maxV.toFixed(2)}`;
            if (uninitialized) { state = 'bad'; tip = 'EKF uninitialized'; }
            else if (maxV >= 1.0) { state = 'bad'; tip = `EKF variance high (${maxV.toFixed(2)})`; }
            else if (maxV >= 0.5) { state = 'warn'; tip = `EKF variance ${maxV.toFixed(2)}`; }
            setEkfStatus({ state, tip });
        }
        if (vehicle.VIBRATION !== undefined) {
            const v = vehicle.VIBRATION;
            const m = Math.max(v.vibration_x || 0, v.vibration_y || 0, v.vibration_z || 0);
            let state = 'ok';
            if (m >= 60) state = 'bad';
            else if (m >= 30) state = 'warn';
            setVibeStatus({ state, tip: `Vibe X/Y/Z: ${(v.vibration_x||0).toFixed(1)} / ${(v.vibration_y||0).toFixed(1)} / ${(v.vibration_z||0).toFixed(1)}` });
        }
        // RC receiver RSSI (radio link from transmitter to aircraft).
        // 0..254 valid, 255 = unknown per MAVLink spec.
        if (vehicle.RC_CHANNELS !== undefined && typeof vehicle.RC_CHANNELS.rssi === 'number') {
            const raw = vehicle.RC_CHANNELS.rssi;
            setRcRssi(raw === 255 ? null : Math.round((raw / 254) * 100));
        } else if (vehicle.RC_CHANNELS_RAW !== undefined && typeof vehicle.RC_CHANNELS_RAW.rssi === 'number') {
            const raw = vehicle.RC_CHANNELS_RAW.rssi;
            setRcRssi(raw === 255 ? null : Math.round((raw / 254) * 100));
        }
        // Telemetry (SiK) modem RSSI
        if (vehicle.RADIO_STATUS !== undefined) {
            const r = vehicle.RADIO_STATUS;
            if (typeof r.rssi === 'number') setTlmRssi(Math.round((r.rssi / 255) * 100));
            if (typeof r.remrssi === 'number') setTlmRemRssi(Math.round((r.remrssi / 255) * 100));
        } else if (vehicle.RADIO !== undefined) {
            const r = vehicle.RADIO;
            if (typeof r.rssi === 'number') setTlmRssi(Math.round((r.rssi / 255) * 100));
            if (typeof r.remrssi === 'number') setTlmRemRssi(Math.round((r.remrssi / 255) * 100));
        }
    }, [mavTimestamp, selectedMavId]);

    const barBg = 'rgba(0, 0, 0, 0.7)';
    const barBorder = '1px solid rgba(255, 255, 255, 0.2)';
    const cellSx = {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: 0.25,
        px: 1,
        py: 0.5,
    };

    return (
        <Box
            sx={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                color: theme.palette.text.primary,
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    gap: 0.5,
                    mb: 0.5,
                    pointerEvents: 'auto',
                }}
            >
                <StatusChip label="EKF" state={ekfStatus.state} tooltip={ekfStatus.tip} />
                <StatusChip label="VIBE" state={vibeStatus.state} tooltip={vibeStatus.tip} />
            </Box>
            <Box sx={{ position: 'relative', display: 'flex', alignItems: 'stretch', gap: 0.5 }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'stretch',
                        bgcolor: barBg,
                        border: barBorder,
                        borderRadius: 1,
                        boxShadow: '0 2px 6px rgba(0, 0, 0, 0.5)',
                        backdropFilter: 'blur(2px)',
                        pointerEvents: 'auto',
                    }}
                >
                    <RssiBar
                        label="RC"
                        percent={rcRssi}
                        tooltip={rcRssi === null ? 'RC RSSI unavailable' : `RC receiver RSSI: ${rcRssi}%`}
                    />
                </Box>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'stretch',
                    bgcolor: barBg,
                    border: barBorder,
                    borderRadius: 1,
                    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.5)',
                    backdropFilter: 'blur(2px)',
                    pointerEvents: 'auto',
                }}
            >
                <Box sx={{ ...cellSx, minWidth: 100, borderRight: barBorder }}>
                    <InfoRow label="ALT" value={altitude.toFixed(1)} unit="m" />
                    <InfoRow label="RALT" value={relativeAltitude.toFixed(1)} unit="m" />
                </Box>
                <Box sx={{ ...cellSx, minWidth: 100, borderRight: barBorder }}>
                    <InfoRow label="GS" value={groundspeed.toFixed(1)} unit="m/s" />
                    <InfoRow label="VS" value={climb.toFixed(1)} unit="m/s" />
                </Box>
                {/* spacer beneath the attitude indicator (also hosts it via absolute positioning) */}
                <Box sx={{ width: HUD_SIZE, borderRight: barBorder, position: 'relative' }}>
                    <Box
                        className="hud"
                        sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            width: HUD_SIZE,
                            height: HUD_SIZE,
                            pointerEvents: 'auto',
                        }}
                    >
                        <AttitudeIndicator
                            className="hud-attitude-indicator"
                            selectedMavId={selectedMavId}
                            mavTimestamp={mavTimestamp}
                            size={HUD_SIZE}
                        />
                        <Box
                            sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                pointerEvents: 'none',
                                px: 1,
                                py: 0.125,
                                borderRadius: 1,
                                bgcolor: 'rgba(0, 0, 0, 0.75)',
                                border: '1px solid rgba(255, 255, 255, 0.25)',
                                boxShadow: '0 2px 6px rgba(0, 0, 0, 0.5)',
                                color: '#fff',
                                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                                fontVariantNumeric: 'tabular-nums',
                                lineHeight: 1,
                            }}
                        >
                            <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                                {Math.round(heading)}°
                            </Typography>
                        </Box>
                    </Box>
                </Box>
                <Box sx={{ ...cellSx, minWidth: 100, borderRight: barBorder }}>
                    {batteryVoltage !== null ? (
                        <InfoRow
                            label="BAT"
                            value={batteryVoltage.toFixed(1)}
                            unit={
                                batteryRemaining !== null && batteryRemaining >= 0
                                    ? `V·${batteryRemaining}%`
                                    : 'V'
                            }
                        />
                    ) : (
                        <InfoRow label="BAT" value="--" unit="V" />
                    )}
                    <InfoRow
                        label="SAT"
                        value={satellites !== null ? satellites : '--'}
                        unit=""
                    />
                </Box>
                <Box sx={{ ...cellSx, minWidth: 100 }}>
                    <InfoRow label="THR" value={`${Math.round(throttle)}`} unit="%" />
                    <InfoRow label="AS" value={airspeed.toFixed(1)} unit="m/s" />
                </Box>
            </Box>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'stretch',
                        bgcolor: barBg,
                        border: barBorder,
                        borderRadius: 1,
                        boxShadow: '0 2px 6px rgba(0, 0, 0, 0.5)',
                        backdropFilter: 'blur(2px)',
                        pointerEvents: 'auto',
                    }}
                >
                    <RssiBar
                        label="TLM"
                        percent={tlmRssi}
                        tooltip={
                            tlmRssi === null
                                ? 'Telemetry RSSI unavailable'
                                : `Telemetry RSSI ${tlmRssi}%${tlmRemRssi !== null ? ` (remote ${tlmRemRssi}%)` : ''}`
                        }
                    />
                </Box>
            </Box>
        </Box>
    );
};

export default Hud;
